import { ExpandMoreRounded, SmartToyOutlined } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Divider,
    Paper,
    Typography,
} from "@mui/material";
import React from "react";
import {
    KboExploration,
    KboExplorationNode,
    KboNodeAnalysis,
    KboNodeAnalysisResult,
    KboNodeFinding,
    KboNodeFocus,
    KboNodeSelectedFinding,
    KboNodeVisual,
} from "./KboFlowRunDebug";
import {
    Field,
    formatDuration,
    formatNumber,
    JsonCard,
    SectionHeading,
    SectionStack,
    shortId,
    StatTile,
} from "./KboFlowDebugUI";
import { InvocationRow, invocationStatusColor } from "./KboFlowRunSections";

/** Formats a 0..1 fraction as a percentage, e.g. 0.241 -> "24.1%". */
function formatPercent(value?: number): string {
    if (typeof value !== "number" || Number.isNaN(value)) return "—";
    return new Intl.NumberFormat(undefined, {
        style: "percent",
        maximumFractionDigits: 1,
    }).format(value);
}

/**
 * Orders exploration nodes depth-first: each node is immediately followed by its
 * children, so the flat list reads as an indented tree. Nodes whose parent is not in
 * the set (or absent) are treated as roots. Any nodes left unreached (cycles / dangling
 * parents) are appended so nothing is silently dropped.
 */
function orderNodesDepthFirst(
    nodes: KboExplorationNode[],
): KboExplorationNode[] {
    const childrenByParent = new Map<string, KboExplorationNode[]>();
    const ids = new Set(nodes.map((n) => n.nodeId));
    const roots: KboExplorationNode[] = [];

    nodes.forEach((node) => {
        const parent = node.parentNodeId;
        if (parent && ids.has(parent)) {
            const siblings = childrenByParent.get(parent) ?? [];
            siblings.push(node);
            childrenByParent.set(parent, siblings);
        } else {
            roots.push(node);
        }
    });

    const ordered: KboExplorationNode[] = [];
    const seen = new Set<string>();
    const visit = (node: KboExplorationNode) => {
        if (node.nodeId && seen.has(node.nodeId)) return;
        if (node.nodeId) seen.add(node.nodeId);
        ordered.push(node);
        (childrenByParent.get(node.nodeId ?? "") ?? []).forEach(visit);
    };
    roots.forEach(visit);
    // Append any nodes not reached from a root (defensive against dangling parents).
    nodes.forEach((node) => {
        if (!node.nodeId || !seen.has(node.nodeId)) {
            seen.add(node.nodeId ?? "");
            ordered.push(node);
        }
    });
    return ordered;
}

/** A single analysis finding: headline claim + supporting reasoning and coverage. */
function FindingCard({ finding }: { finding: KboNodeFinding }) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box
                sx={{
                    alignItems: "baseline",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {finding.name ?? "Finding"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {typeof finding.conversationCount === "number" && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(finding.conversationCount)} of ${formatNumber(
                            finding.totalConversationsReviewed,
                        )} · ${formatPercent(finding.conversationShare)}`}
                    />
                )}
            </Box>
            {finding.claim && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {finding.claim}
                </Typography>
            )}
            {finding.reasoning && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {finding.reasoning}
                </Typography>
            )}
        </Paper>
    );
}

/** One analysis pass result: coverage stats, findings, and the raw distribution. */
function AnalysisResultCard({ result }: { result: KboNodeAnalysisResult }) {
    const findings = result.findings ?? [];
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {result.reasoningModelName && (
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<SmartToyOutlined />}
                        label={result.reasoningModelName}
                    />
                )}
                {typeof result.reviewedMaterialCount === "number" && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(result.reviewedMaterialCount)} reviewed`}
                    />
                )}
                {typeof result.reviewedMaterialCoverage === "number" && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatPercent(result.reviewedMaterialCoverage)} coverage`}
                    />
                )}
                {result.materialSource && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={result.materialSource}
                    />
                )}
            </Box>
            {findings.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mb: 1,
                    }}
                >
                    {findings.map((finding, index) => (
                        <FindingCard key={index} finding={finding} />
                    ))}
                </Box>
            )}
            {result.distribution !== undefined &&
                result.distribution !== null && (
                    <JsonCard title="Distribution" data={result.distribution} />
                )}
        </Paper>
    );
}

/** Findings the node selected to act on, and whether each spawned a deeper node. */
function SelectedFindingCard({
    selected,
}: {
    selected: KboNodeSelectedFinding;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box
                sx={{
                    alignItems: "baseline",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {selected.finding?.name ?? "Selected finding"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip
                    size="small"
                    color={selected.exploreDeeper ? "info" : "default"}
                    variant="outlined"
                    label={
                        selected.exploreDeeper
                            ? "explored deeper"
                            : "not explored"
                    }
                />
            </Box>
            {selected.finding?.claim && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selected.finding.claim}
                </Typography>
            )}
            {selected.reason && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {selected.reason}
                </Typography>
            )}
        </Paper>
    );
}

/** A pre-rendered HTML visual, shown in a sandboxed iframe (same pattern as reports). */
function VisualCard({ visual }: { visual: KboNodeVisual }) {
    return (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Box sx={{ backgroundColor: "gray.100", px: 1.5, py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {visual.title ?? visual.kind ?? "Visual"}
                </Typography>
                {visual.caption && (
                    <Typography variant="caption" color="text.secondary">
                        {visual.caption}
                    </Typography>
                )}
            </Box>
            {visual.markup ? (
                <Box
                    component="iframe"
                    title={visual.title ?? "Visual"}
                    srcDoc={visual.markup}
                    sandbox=""
                    sx={{
                        border: 0,
                        display: "block",
                        height: 360,
                        width: "100%",
                    }}
                />
            ) : (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 1.5 }}
                >
                    No markup.
                </Typography>
            )}
        </Paper>
    );
}

function NodeFocusPanel({ focus }: { focus: KboNodeFocus }) {
    const findings = focus.establishedFindings ?? [];
    return (
        <SectionStack sx={{ gap: 1 }}>
            <SectionHeading>Focus</SectionHeading>
            {focus.parentClaim && (
                <Field label="Parent claim" value={focus.parentClaim} />
            )}
            {typeof focus.startCohortCount === "number" && (
                <Field
                    label="Start cohort count"
                    value={formatNumber(focus.startCohortCount)}
                />
            )}
            {findings.length > 0 && (
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Established findings
                    </Typography>
                    <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                        {findings.map((text, index) => (
                            <li key={index}>
                                <Typography variant="body2">{text}</Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}
            {focus.startCohort !== undefined && focus.startCohort !== null && (
                <JsonCard title="Start Cohort" data={focus.startCohort} />
            )}
        </SectionStack>
    );
}

function NodeAnalysisPanel({ analysis }: { analysis: KboNodeAnalysis }) {
    const results = analysis.results ?? [];
    const selected = analysis.selected ?? [];
    const visuals = analysis.visuals ?? [];
    return (
        <SectionStack sx={{ gap: 1 }}>
            <SectionHeading>Analysis</SectionHeading>
            {analysis.dimension && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Field
                        label="Dimension"
                        value={analysis.dimension.alias}
                    />
                    {analysis.dimension.reason && (
                        <Field
                            label="Reason"
                            value={analysis.dimension.reason}
                        />
                    )}
                    {typeof analysis.analyzedCohortCount === "number" && (
                        <Field
                            label="Analyzed cohort"
                            value={formatNumber(analysis.analyzedCohortCount)}
                        />
                    )}
                    {(analysis.dimension.considered?.length ||
                        analysis.dimension.requiredFilters?.length) && (
                        <JsonCard
                            title="Dimension detail"
                            data={{
                                considered: analysis.dimension.considered,
                                requiredFilters:
                                    analysis.dimension.requiredFilters,
                            }}
                        />
                    )}
                </Paper>
            )}
            {analysis.analyzedCohort !== undefined &&
                analysis.analyzedCohort !== null && (
                    <JsonCard
                        title="Analyzed Cohort"
                        data={analysis.analyzedCohort}
                    />
                )}
            {results.length > 0 && (
                <Box>
                    <SectionHeading count={results.length}>
                        Results
                    </SectionHeading>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 0.5,
                        }}
                    >
                        {results.map((result, index) => (
                            <AnalysisResultCard key={index} result={result} />
                        ))}
                    </Box>
                </Box>
            )}
            {selected.length > 0 && (
                <Box>
                    <SectionHeading count={selected.length}>
                        Selected Findings
                    </SectionHeading>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 0.5,
                        }}
                    >
                        {selected.map((item, index) => (
                            <SelectedFindingCard key={index} selected={item} />
                        ))}
                    </Box>
                </Box>
            )}
            {visuals.length > 0 && (
                <Box>
                    <SectionHeading count={visuals.length}>
                        Visuals
                    </SectionHeading>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 0.5,
                        }}
                    >
                        {visuals.map((visual, index) => (
                            <VisualCard
                                key={visual.visualId ?? index}
                                visual={visual}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </SectionStack>
    );
}

function ExplorationNodeRow({ node }: { node: KboExplorationNode }) {
    const depth = node.depth ?? 0;
    const invocations = node.invocations ?? [];
    return (
        <Box
            sx={{
                borderLeft: depth > 0 ? "2px solid" : "none",
                borderColor: "lightgrayBorder.main",
                ml: depth > 0 ? depth * 2 : 0,
                pl: depth > 0 ? 1.5 : 0,
            }}
        >
            <Accordion
                disableGutters
                TransitionProps={{ unmountOnExit: true }}
                sx={{
                    "&:before": { display: "none" },
                    border: "1px solid",
                    borderColor: "lightgrayBorder.main",
                    borderRadius: 1,
                    overflow: "hidden",
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            pr: 2,
                            width: "100%",
                        }}
                    >
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`depth ${depth}`}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {node.analysis?.dimension?.alias ??
                                node.focus?.parentClaim ??
                                shortId(node.nodeId)}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                            size="small"
                            color={invocationStatusColor(node.status)}
                            label={node.status ?? "—"}
                        />
                        {typeof node.analysis?.analyzedCohortCount ===
                            "number" && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${formatNumber(
                                    node.analysis.analyzedCohortCount,
                                )} convos`}
                            />
                        )}
                        {invocations.length > 0 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${invocations.length} LLM call${
                                    invocations.length === 1 ? "" : "s"
                                }`}
                            />
                        )}
                        <Chip
                            size="small"
                            variant="outlined"
                            label={formatDuration(
                                node.startedAtUtc,
                                node.completedAtUtc,
                            )}
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <SectionStack sx={{ gap: 2 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            <Field label="Node ID" value={shortId(node.nodeId)} />
                            {node.parentNodeId && (
                                <Field
                                    label="Parent"
                                    value={shortId(node.parentNodeId)}
                                />
                            )}
                        </Box>
                        {node.error && (
                            <Box>
                                <Typography variant="subtitle2" color="error">
                                    Error
                                </Typography>
                                <Typography variant="body2" color="error">
                                    {node.error}
                                </Typography>
                            </Box>
                        )}
                        {node.focus && <NodeFocusPanel focus={node.focus} />}
                        {node.analysis && (
                            <NodeAnalysisPanel analysis={node.analysis} />
                        )}
                        {invocations.length > 0 && (
                            <Box>
                                <SectionHeading count={invocations.length}>
                                    Invocations
                                </SectionHeading>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        mt: 0.5,
                                    }}
                                >
                                    {invocations.map((invocation, index) => (
                                        <InvocationRow
                                            key={index}
                                            invocation={invocation}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </SectionStack>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export function ExplorationSection({
    exploration,
}: {
    exploration: KboExploration;
}) {
    const nodes = exploration.nodes ?? [];
    if (nodes.length === 0) {
        return (
            <Typography variant="body2">
                No exploration nodes recorded.
            </Typography>
        );
    }

    const ordered = orderNodesDepthFirst(nodes);
    const completed = nodes.filter(
        (n) => (n.status ?? "").toLowerCase() === "completed",
    ).length;
    const failed = nodes.filter(
        (n) => (n.status ?? "").toLowerCase() === "failed",
    ).length;
    const maxDepth = nodes.reduce((max, n) => Math.max(max, n.depth ?? 0), 0);
    const totalInvocations = nodes.reduce(
        (sum, n) => sum + (n.invocations?.length ?? 0),
        0,
    );

    return (
        <SectionStack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <StatTile label="Nodes" value={nodes.length} />
                <StatTile
                    label="Completed"
                    value={completed}
                    accent="success.main"
                />
                <StatTile
                    label="Failed"
                    value={failed}
                    accent={failed > 0 ? "error.main" : undefined}
                />
                <StatTile label="Max Depth" value={maxDepth} />
                <StatTile label="Invocations" value={totalInvocations} />
            </Box>
            {exploration.explorationId && (
                <Field
                    label="Exploration ID"
                    value={exploration.explorationId}
                />
            )}
            <Divider />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {ordered.map((node, index) => (
                    <ExplorationNodeRow
                        key={node.nodeId ?? index}
                        node={node}
                    />
                ))}
            </Box>
        </SectionStack>
    );
}
